import { useMemo } from 'react'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { Request } from '@omnea-app/requests/requestsHttpClient'

export type RequestParticipants = Pick<Request, 'participants'> & {
  derivedFields: Pick<Request['derivedFields'], 'currentGroupAssignees' | 'currentUserAssignees'>
}

/**
 * Whether the caller takes part in this request — the participant floor these surfaces carried before
 * the Permissions Service, and what still decides for a record it does not answer for.
 *
 * This is not the `derived_requester or derived_collaborator` set the request verbs carry, in either
 * direction: the business owner passes here but writes no tuple, while past assignees and followers
 * hold the relation and fail here, since only in-progress steps reach `current*Assignees`.
 */
export const useIsAttachedToRequest = (
  request: RequestParticipants | undefined,
  // Off where the backend counts participation without the business owner (`includeSupplierContacts: false`).
  { includeBusinessOwner = true }: { includeBusinessOwner?: boolean } = {}
): boolean => {
  const { user } = useAuthenticatedUser()

  return useMemo(() => {
    if (request == null) {
      return false
    }
    const { participants, derivedFields } = request
    const isRequestor = participants?.requestor?.id === user.id
    const isProjectOwner = participants?.projectOwner?.id === user.id
    const isBusinessOwner = includeBusinessOwner && participants?.businessOwner?.id === user.id
    const isUserAssignee = derivedFields?.currentUserAssignees?.some((assignee) => assignee.id === user.id) ?? false
    const isGroupAssignee = derivedFields?.currentGroupAssignees?.some((group) => group.userIds.includes(user.id)) ?? false

    return isRequestor || isProjectOwner || isBusinessOwner || isUserAssignee || isGroupAssignee
  }, [user.id, request, includeBusinessOwner])
}
